import { selectFeatureFlags } from 'core/userContext';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { BUTTON_SIZES, GhostButton } from 'ui/button';
import { FilterMenuTriggerButton } from 'ui/indexPages/filters/FilterMenuTriggerButton';
import { Menu, MenuItemOption } from 'ui/menu';
import { ALIGNMENT } from 'ui/menu/constants';
import { Overline } from 'ui/theme';
import {
  EMAILS_SENT,
  IN_APP_NOTIFICATIONS_SENT,
  MESSAGING_METRICS,
  PROJECT_NAME_COLUMN_VALUE,
  PUSH_NOTIFICATIONS_SENT,
  SMS_METRICS,
  WEB_PUSH_NOTIFICATIONS_SENT,
  WHATSAPP_SENT,
} from '../../constants';
import { mapMetricToOption, mapTextToOptionTitle } from '../../utils';
import { useSimpleIndexContext } from '../SimpleIndexPage/contexts';
import { SmsTitle } from './PageActions.styled';

const smsMetricNames = Object.values(SMS_METRICS);

export const MessagingTableFilters = () => {
  const { ExpandedChannelMetrics, WhatsAppMessageChannelGA } = useSelector(selectFeatureFlags);
  const { activeColumns, setActiveColumns } = useSimpleIndexContext();

  const defaultColumns = useMemo(() => {
    return [
      PROJECT_NAME_COLUMN_VALUE,
      MESSAGING_METRICS.emailsSent,
      SMS_METRICS.smsSegmentsSent,
      SMS_METRICS.smsSegmentsReceived,
      SMS_METRICS.mmsAttachmentsSent,
      SMS_METRICS.mmsAttachmentsReceived,
      MESSAGING_METRICS.pushNotificationsSent,
      ...(ExpandedChannelMetrics
        ? [MESSAGING_METRICS.webPushNotificationsSent, MESSAGING_METRICS.inAppNotificationsSent]
        : []),
      ...(WhatsAppMessageChannelGA ? [MESSAGING_METRICS.whatsAppSent] : []),
    ];
  }, [ExpandedChannelMetrics, WhatsAppMessageChannelGA]);

  /** set active columns causes a quick flash in page */
  useEffect(() => {
    setActiveColumns(defaultColumns);
  }, [defaultColumns, setActiveColumns]);

  /** -1 because Project name column is not selectable in filter options */
  const selectedFilterCount = activeColumns.length - 1;

  const handleSelection = (val: string) => {
    const next = activeColumns.includes(val)
      ? activeColumns.filter((activeFilterOption) => activeFilterOption !== val)
      : [...activeColumns, val];
    setActiveColumns(next);
  };

  const areAllSmsMetricsSelected = smsMetricNames.every((metric) => activeColumns.includes(metric));
  const toggleAllSms = () => {
    const next = areAllSmsMetricsSelected
      ? activeColumns.filter(
          (column) => column === PROJECT_NAME_COLUMN_VALUE || !smsMetricNames.includes(column),
        )
      : [...new Set([...activeColumns, ...smsMetricNames])];
    setActiveColumns(next);
  };

  const SMS_FILTER_SECTION_TITLE = {
    id: 'SMS',
    content: (
      <SmsTitle>
        <Overline>SMS</Overline>
        <GhostButton size={BUTTON_SIZES.SMALL} onClick={toggleAllSms}>
          {areAllSmsMetricsSelected ? 'Select none' : 'Select all'}
        </GhostButton>
      </SmsTitle>
    ),
    isTitle: true,
  };

  const filterOptions: MenuItemOption[] = [
    mapTextToOptionTitle('Email'),
    mapMetricToOption(EMAILS_SENT),
    SMS_FILTER_SECTION_TITLE,
    ...smsMetricNames.map(mapMetricToOption),
    mapTextToOptionTitle('Push'),
    mapMetricToOption(PUSH_NOTIFICATIONS_SENT),
    ...(ExpandedChannelMetrics
      ? [
          mapTextToOptionTitle('In-app'),
          mapMetricToOption(IN_APP_NOTIFICATIONS_SENT),
          mapTextToOptionTitle('Web push'),
          mapMetricToOption(WEB_PUSH_NOTIFICATIONS_SENT),
        ]
      : []),
    ...(WhatsAppMessageChannelGA
      ? [mapTextToOptionTitle('WhatsApp'), mapMetricToOption(WHATSAPP_SENT)]
      : []),
  ];

  return (
    <Menu
      defaultSelectedOptionIds={activeColumns}
      handleSelectionChange={handleSelection}
      hasFilter={false}
      horizontalAlignment={ALIGNMENT.LEFT}
      isSelectMenu={true}
      options={filterOptions}
    >
      <FilterMenuTriggerButton triggerText="Columns" selectedFilterCount={selectedFilterCount} />
    </Menu>
  );
};
